import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { rankingsAPI, trialPeriodsAPI } from '../services/api';
import './Rankings.css';

interface Ranking {
  id: string;
  rank: number;
  totalScore: number;
  metricsEvaluated: number;
  player: {
    id: string;
    firstName: string;
    lastName: string;
    position?: string;
    team?: {
      id: string;
      name: string;
    };
  };
}

interface TrialPeriod {
  id: string;
  name: string;
  season: string;
  year: number;
  quarter: number;
}

const Rankings: React.FC = () => {
  const navigate = useNavigate();
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  const [categoryType, setCategoryType] = useState<'overall' | 'physical' | 'technical'>('overall');
  const [trialPeriods, setTrialPeriods] = useState<TrialPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('overall');

  useEffect(() => {
    loadTrialPeriods();
  }, []);

  useEffect(() => {
    loadRankings();
  }, [categoryType, selectedPeriod]);

  const loadTrialPeriods = async () => {
    try {
      const response = await trialPeriodsAPI.getAll();
      setTrialPeriods(response.data);
    } catch (error) {
      console.error('Failed to load trial periods:', error);
    }
  };

  const loadRankings = async () => {
    try {
      setLoading(true);
      const trialPeriodId = selectedPeriod === 'overall' ? undefined : selectedPeriod;
      const response = await rankingsAPI.getAll(undefined, categoryType, trialPeriodId);
      setRankings(response.data);
      
      // Extract unique team names
      const teams = new Set<string>();
      response.data.forEach((ranking: Ranking) => {
        if (ranking.player.team?.name) {
          teams.add(ranking.player.team.name);
        }
      });
      setAvailableTeams(Array.from(teams).sort());
    } catch (error) {
      console.error('Failed to load rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamToggle = (teamName: string) => {
    setSelectedTeams(prev => 
      prev.includes(teamName)
        ? prev.filter(t => t !== teamName)
        : [...prev, teamName]
    );
  };

  const filteredRankings = React.useMemo(() => {
    let filtered = rankings;
    
    // Filter by selected teams
    if (selectedTeams.length > 0) {
      filtered = rankings.filter(ranking => 
        ranking.player.team?.name && selectedTeams.includes(ranking.player.team.name)
      );
    }
    
    // Recalculate ranks for the filtered players (1, 2, 3, etc.)
    return filtered.map((ranking, index) => ({
      ...ranking,
      rank: index + 1
    }));
  }, [rankings, selectedTeams]);

  if (loading) {
    return <div className="loading">Loading rankings...</div>;
  }

  return (
    <div className="rankings-page">
      <div className="hero-section">
        <h1>Player Rankings</h1>
        <p>Rankings based on average performance across all 13 evaluation categories</p>
      </div>

      <div className="rankings-container">
        <div className="rankings-header">
          <div className="header-content">
            <div>
              <h2>
                {categoryType === 'overall' && 'Overall Rankings'}
                {categoryType === 'physical' && 'Physical Rankings'}
                {categoryType === 'technical' && 'Technical Rankings'}
              </h2>
              <p className="subtitle">Lower total score = better overall performance</p>
            </div>
            <div className="category-selector">
              <label htmlFor="category-type">Ranking Type:</label>
              <select
                id="category-type"
                value={categoryType}
                onChange={(e) => setCategoryType(e.target.value as 'overall' | 'physical' | 'technical')}
                className="category-dropdown"
              >
                <option value="overall">Overall</option>
                <option value="physical">Physical</option>
                <option value="technical">Technical</option>
              </select>
            </div>
            <div className="category-selector">
              <label htmlFor="trial-period">Trial Period:</label>
              <select
                id="trial-period"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="category-dropdown"
              >
                <option value="overall">Overall (All Time)</option>
                {trialPeriods.map(period => (
                  <option key={period.id} value={period.id}>
                    {period.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {availableTeams.length > 0 && (
          <div className="filter-section">
            <h3>Filter by Team</h3>
            <div className="team-filters">
              {availableTeams.map(team => (
                <label key={team} className="team-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedTeams.includes(team)}
                    onChange={() => handleTeamToggle(team)}
                  />
                  <span>{team}</span>
                </label>
              ))}
            </div>
            {selectedTeams.length > 0 && (
              <button 
                className="clear-filters-btn"
                onClick={() => setSelectedTeams([])}
              >
                Clear Filters ({selectedTeams.length} selected)
              </button>
            )}
          </div>
        )}

        <div className="rankings-table">
          <table>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Player</th>
                <th>Team</th>
                <th>Position</th>
                <th>Total Score</th>
              </tr>
            </thead>
            <tbody>
              {filteredRankings.map((ranking) => (
                <tr key={ranking.id} className={ranking.rank <= 3 ? 'top-rank' : ''}>
                  <td className="rank-cell">
                    <span className={`rank-badge rank-${ranking.rank}`}>
                      {ranking.rank}
                    </span>
                  </td>
                  <td className="player-cell">
                    <button 
                      className="player-name-link"
                      onClick={() => navigate(`/admin/player/${ranking.player.id}/evaluations`)}
                      title="View player evaluations"
                    >
                      <strong>{ranking.player.firstName} {ranking.player.lastName}</strong>
                    </button>
                  </td>
                  <td>{ranking.player.team?.name || 'N/A'}</td>
                  <td>{ranking.player.position || '-'}</td>
                  <td><strong>{ranking.totalScore}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredRankings.length === 0 && rankings.length > 0 && (
            <div className="no-data">No players found for selected teams</div>
          )}
          {rankings.length === 0 && (
            <div className="no-data">No rankings available</div>
          )}
        </div>

        <div className="rankings-info">
          <h3>How Rankings Work</h3>
          <p>
            <strong>Total Score:</strong> Sum of player's rank in EVERY individual metric across all categories. 
            Each metric (including Left Foot and Right Foot separately) is ranked independently. Players who didn't 
            participate in a metric are tied for last place in that metric. Lower total score = better overall ranking.
          </p>
          <p className="example">
            <strong>Example:</strong> If a player ranked 1st, 15th, 28th, and 20th in four different metrics, 
            their total score would be 64 (1+15+28+20).
          </p>
        </div>
      </div>
    </div>
  );
};

export default Rankings;
